import React from 'react'

import { useTranslation } from 'react-i18next'

import { ArcadeFieldLabel, BirthdateInput as CDSBirthdateInput, FieldLabel } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { Region, useRegion } from '@cash-web/shared-data-access-region-provider'
import { isValidBirthdate } from '@cash-web/shared-util-validation'

import { FieldController } from '../FieldController/FieldController'
import { FormMaskedInput } from '../FormMaskedInput'

type BirthdateInputProps = {
  name: string
  isRequired: boolean
}

// If you need to add another region, make sure the placeholder for that region's
// date matches what is expected in that region.
const supportedRegions = ['US', 'GB'] as const
type SupportedRegion = (typeof supportedRegions)[number]
function isSupportedRegion(region: Region): region is SupportedRegion {
  return supportedRegions.includes(region as SupportedRegion)
}

// This input will accept dates in the format MM/DD/YYYY for US users, and
// DD/MM/YYYY for GB users but will always report the values in the form MM/DD/YYYY
export function BirthdateInput({ isRequired, name }: BirthdateInputProps) {
  const { t } = useTranslation()
  const regionCode = useRegion()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  if (regionCode && !isSupportedRegion(regionCode)) {
    throw Error(`BirthdateInput: unexpected region ${regionCode}`)
  }

  const placeholder =
    regionCode === 'US'
      ? t('MM/DD/YYYY', { context: 'placeholder for form field asking for birthday date' })
      : t('DD/MM/YYYY', { context: 'placeholder for form field asking for birthday date' })
  const requiredErrorMsg = t('Enter your birthdate.', {
    context: 'error message for form field when missing a birthday date',
  })
  const invalidErrorMsg = t('Enter a valid birthdate.', {
    context: 'placeholder for form field for an invalid birthday date',
  })

  if (isArcadeMigrationEnabled) {
    return (
      <ArcadeFieldLabel label={t('Date of birth', { context: "label for form field asking for a user's birthdate" })}>
        <FieldController
          FieldComponent={CDSBirthdateInput}
          componentProps={{
            placeholder,
            inputFormat: regionCode === 'GB' ? 'day-first' : 'month-first',
            outputFormat: 'month-first',
          }}
          name={name}
          rules={{
            required: { value: isRequired, message: requiredErrorMsg },
            validate: {
              // Only validate birthdate if it's been filled in
              format: (v: string) => !v || isValidBirthdate(v) || invalidErrorMsg,
            },
          }}
        />
      </ArcadeFieldLabel>
    )
  }

  return (
    <FieldLabel label={t('Date of birth', { context: "label for form field asking for a user's birthdate" })}>
      <FormMaskedInput
        name={name}
        format="##/##/####"
        placeholder={placeholder}
        rules={{
          required: { value: isRequired, message: requiredErrorMsg },
          validate: {
            // Only validate birthdate if it's been filled in
            format: v => !v || isValidBirthdate(v) || invalidErrorMsg,
          },
        }}
        transform={(unformattedDate: string) => (regionCode ? usDateFormat(unformattedDate, regionCode) : undefined)}
      />
    </FieldLabel>
  )
}

// GB dates will have the format ddmmyyyy and that needs to be converted to
// mm/dd/yyyy
function usDateFormat(date: string, currentDateFormat: 'US' | 'GB') {
  if (date === '') {
    return ''
  }
  if (currentDateFormat === 'GB') {
    const day = date.slice(0, 2)
    const month = date.slice(2, 4)
    const year = date.slice(4)

    return `${month}/${day}/${year}`
  }

  // TODO - should we be defaulting to US formatting if the region is not GB?
  const month = date.slice(0, 2)
  const day = date.slice(2, 4)
  const year = date.slice(4)

  return `${month}/${day}/${year}`
}
